import { useEffect, useState } from "react";
import { UserRound, Building2, KeyRound, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function StoreAccount() {
  const { user, setUser } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [profile, setProfile] = useState({ name: "", email: "", phone: "", avatar: "" });
  const [storeName, setStoreName] = useState("");
  const [biz, setBiz] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/store-admin/settings").then((r) => {
      setStoreName(r.data.name || "");
      setBiz(r.data.business_info || {});
      setLoaded(true);
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "", phone: user.phone || "", avatar: user.avatar || "" });
  }, [user]);

  if (!loaded || !user) return <LoadingRows rows={5} />;

  const uploadImage = async (e, cb) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      cb(data.url);
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const saveProfile = async () => {
    setBusy("profile");
    try {
      const { data } = await api.patch("/store-admin/profile", profile);
      setUser(data);
      toast.success("Personal information updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const saveBusiness = async () => {
    setBusy("biz");
    try {
      await api.patch("/store-admin/settings", { name: storeName.trim() || undefined, business_info: biz });
      toast.success("Business information updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setBusy("pw");
    try {
      await api.post("/store-admin/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const setP = (k, v) => setProfile({ ...profile, [k]: v });
  const setB = (k, v) => setBiz({ ...biz, [k]: v });

  return (
    <div data-testid="store-account">
      <PageHeader title="Account Settings" subtitle="Your personal profile, security and business identity." />

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-card border border-border rounded-md p-7 space-y-4">
          <p className="overline-label flex items-center gap-2"><UserRound className="h-4 w-4" /> Personal Information</p>
          <div className="flex items-center gap-4">
            <div className="relative h-16 w-16 rounded-full bg-muted overflow-hidden shrink-0 group" data-testid="avatar-preview">
              {profile.avatar && <img src={fileUrl(profile.avatar)} alt="" className="h-full w-full object-cover" />}
              <label data-testid="avatar-upload" className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer text-white">
                <Camera className="h-5 w-5" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, (url) => setP("avatar", url))} />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Profile photo<br />Hover to upload a new one.</p>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Full Name</Label>
            <Input data-testid="profile-name" value={profile.name} onChange={(e) => setP("name", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="overline-label">Email Address</Label>
              <Input data-testid="profile-email" type="email" value={profile.email} onChange={(e) => setP("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Phone Number</Label>
              <Input data-testid="profile-phone" value={profile.phone} onChange={(e) => setP("phone", e.target.value)} /></div>
          </div>
          <Button data-testid="save-profile-button" onClick={saveProfile} disabled={busy === "profile"} className="rounded-full font-bold">
            {busy === "profile" ? "Saving…" : "Save Personal Info"}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-md p-7 space-y-4">
          <p className="overline-label flex items-center gap-2"><KeyRound className="h-4 w-4" /> Security — Change Password</p>
          <div className="space-y-1.5"><Label className="overline-label">Current Password</Label>
            <Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="overline-label">New Password</Label>
              <Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Confirm New</Label>
              <Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
          </div>
          <p className="text-xs text-muted-foreground">Minimum 8 characters. You'll stay signed in on this device.</p>
          <Button data-testid="change-password-button" onClick={changePassword} disabled={busy === "pw" || !pw.current_password || !pw.new_password} variant="outline" className="rounded-full font-bold">
            {busy === "pw" ? "Updating…" : "Update Password"}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-md p-7 space-y-4 lg:col-span-2">
          <p className="overline-label flex items-center gap-2"><Building2 className="h-4 w-4" /> Business Information</p>
          <div className="flex items-center gap-4">
            <div className="relative h-16 w-16 rounded-md bg-muted overflow-hidden shrink-0 group border border-border" data-testid="logo-preview">
              {biz.logo_url && <img src={fileUrl(biz.logo_url)} alt="" className="h-full w-full object-contain" />}
              <label data-testid="logo-upload" className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer text-white">
                <Camera className="h-5 w-5" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, (url) => setB("logo_url", url))} />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Business logo — automatically shown wherever your store theme displays branding.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="overline-label">Business Name</Label>
              <Input data-testid="biz-name" value={storeName} onChange={(e) => setStoreName(e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Business Phone</Label>
              <Input data-testid="biz-phone" value={biz.phone || ""} onChange={(e) => setB("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Business Email</Label>
              <Input data-testid="biz-email" value={biz.email || ""} onChange={(e) => setB("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Tagline / Description</Label>
              <Input data-testid="biz-description" value={biz.description || ""} onChange={(e) => setB("description", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="overline-label">Business Address</Label>
            <Input data-testid="biz-address" value={biz.address || ""} onChange={(e) => setB("address", e.target.value)} /></div>
          <Button data-testid="save-business-button" onClick={saveBusiness} disabled={busy === "biz"} className="rounded-full font-bold">
            {busy === "biz" ? "Saving…" : "Save Business Info"}
          </Button>
        </div>
      </div>
    </div>
  );
}
